import React, { useState } from 'react';
import { FaTimes, FaHeart, FaComment, FaShare, FaCalendarAlt, FaUser } from 'react-icons/fa';
import { ENDPOINTS } from '../../utils/api-config';
import './ConfirmModal.css';

const ViewPostModal = ({ isOpen, onClose, post }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  if (!isOpen || !post) return null;

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) {
      return url;
    }
    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/auth/login', '').replace('/api', '');
    return `${baseUrl}/${url.replace(/^\/+/, '')}`;
  };

  const images = post.mediaUrls && post.mediaUrls.length > 0 ? post.mediaUrls : [];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="confirm-modal-overlay" onClick={onClose} style={{ zIndex: 99999 }}>
      <div 
        className="confirm-modal-container" 
        style={{ maxWidth: '600px', textAlign: 'left', padding: '0', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {post.author?.avatar ? (
                <img src={getFullUrl(post.author.avatar)} alt="Author" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <FaUser color="#94a3b8" size={20} />
              )}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>
                {post.author?.firstName} {post.author?.lastName}
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#64748b' }}>
                <span style={{ textTransform: 'capitalize', backgroundColor: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                  {post.author?.role || 'User'}
                </span>
                <span>•</span>
                <FaCalendarAlt size={11} />
                <span>{formatDate(post.createdAt)}</span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b', padding: '4px' }}
          >
            <FaTimes />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Post Content */}
          <div style={{ padding: '24px', fontSize: '15px', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            {post.content}
          </div>

          {/* Images */}
          {images.length > 0 && (
            <div style={{ width: '100%', backgroundColor: '#0f172a', position: 'relative' }}>
              <img 
                src={getFullUrl(images[activeImageIndex])} 
                alt="Post attachment" 
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', display: 'block' }} 
              />
              
              {/* Image Navigation */}
              {images.length > 1 && (
                <div style={{ position: 'absolute', bottom: '10px', left: '0', right: '0', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: activeImageIndex === idx ? '#3b82f6' : 'rgba(255,255,255,0.5)',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0
                      }}
                      aria-label={`View image ${idx + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Image Thumbnails if more than 1 */}
          {images.length > 1 && (
             <div style={{ display: 'flex', gap: '8px', padding: '12px 24px', overflowX: 'auto', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
               {images.map((imgUrl, idx) => (
                 <img
                   key={idx}
                   src={getFullUrl(imgUrl)}
                   alt={`Thumbnail ${idx}`}
                   onClick={() => setActiveImageIndex(idx)}
                   style={{
                     width: '60px',
                     height: '60px',
                     objectFit: 'cover',
                     borderRadius: '6px',
                     cursor: 'pointer',
                     border: activeImageIndex === idx ? '2px solid #3b82f6' : '2px solid transparent',
                     opacity: activeImageIndex === idx ? 1 : 0.6,
                     transition: 'all 0.2s'
                   }}
                 />
               ))}
             </div>
          )}

          {/* Stats Bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 24px', backgroundColor: '#ffffff', borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaHeart color={post.isLiked ? "#ef4444" : "#94a3b8"} />
              <span>{post.likesCount || 0} Likes</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaComment />
              <span>{post.commentsCount || 0} Comments</span>
            </div>
            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <FaShare />
              <span>{post.shareCount || 0} Shares</span>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPostModal;
